// index.js
import { createStore } from 'vuex';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import riskAnalysis from './riskAnalysis';
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

export default createStore({
    modules: {
        riskAnalysis
    },
    state() {
        return {
            user: null,
            userProfile: null,
            votingUser: JSON.parse(sessionStorage.getItem('votingUser')) || undefined,
            workshopId: sessionStorage.getItem('workshopId') || undefined,
            userVote: JSON.parse(sessionStorage.getItem('userVote')) || undefined,
            customerId: sessionStorage.getItem('customerId') || undefined,
        };
    },
    mutations: {
        setUser(state, user) {
            state.user = user;
        },
        setUserProfile(state, profile) {
            state.userProfile = profile;
        },
        setWorkshopId(state, workshopId) {
            state.workshopId = workshopId;
        },
        setVotingUser(state, votingUser) {
            state.votingUser = votingUser;
        },
        setUserVote(state, userVote) {
            state.userVote = userVote;
        },
        setCustomerId(state, customerId) {
            state.customerId = customerId;
        },
    },
    actions: {
        fetchUser({ commit }) {
            const auth = getAuth();
            onAuthStateChanged(auth, async (user) => {
                commit('setUser', user);
                if (user) {
                    console.log('[DEBUG] Authenticated as UID:', user.uid);
                    // Check if user exists in RTDB
                    const { getDatabase, ref, get } = await import('firebase/database');
                    const db = getDatabase();
                    const userRef = ref(db, `users/${user.uid}`);
                    try {
                        const snap = await get(userRef);
                        if (snap.exists()) {
                            console.log('[DEBUG] User node in RTDB:', snap.val());
                        } else {
                            console.warn('[DEBUG] No user node found in RTDB for UID:', user.uid);
                        }
                    } catch (err) {
                        console.error('[DEBUG] Error reading user node in RTDB:', err);
                    }
                } else {
                    console.warn('[DEBUG] No user authenticated');
                }
            });
        },
        saveVotingUser({ commit }, votingUser) {
            commit('setVotingUser', votingUser);
            sessionStorage.setItem('votingUser', JSON.stringify(votingUser));
        },
        saveWorkshopId({ commit }, workshopId) {
            commit('setWorkshopId', workshopId);
            sessionStorage.setItem('workshopId', workshopId);
        },
        saveUserVote({ commit }, userVote) {
            commit('setUserVote', userVote);
            sessionStorage.setItem('userVote', JSON.stringify(userVote));
        },
        saveCustomerId({ commit }, customerId) {
            commit('setCustomerId', customerId);
            sessionStorage.setItem('customerId', customerId);
        },
        async fetchUserProfile({ commit, state }) {
            if (!state.user) return;
            // Use Realtime Database instead of Firestore
            const { getDatabase, ref, get, set } = await import('firebase/database');
            const db = getDatabase();
            const userRef = ref(db, `users/${state.user.uid}`);
            const snap = await get(userRef);
            if (snap.exists()) {
                // Add uid and email from auth to the profile object
                const profile = { ...snap.val(), uid: state.user.uid, email: state.user.email };
                console.log('[DEBUG] fetchUserProfile loaded from RTDB:', profile);
                commit('setUserProfile', profile);
            } else {
                // If not found, create a default profile
                const newProfile = {
                    uid: state.user.uid,
                    email: state.user.email,
                    role: 'member',
                    companyId: null,
                    departmentId: null,
                    projectId: null,
                    createdAt: new Date().toISOString()
                };
                await set(userRef, newProfile);
                commit('setUserProfile', newProfile);
            }
        },
        async updateUserProfile({ commit, state }, updates) {
            if (!state.user) return;
            const userRef = doc(db, 'users', state.user.uid);
            await updateDoc(userRef, updates);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                commit('setUserProfile', userSnap.data());
            }
        }
    },
    getters: {
        currentUser(state) {
            return state.user;
        },
        userProfile(state) {
            return state.userProfile;
        },
        getVotingUser: (state) => state.votingUser,
        getWorkshopId: (state) => state.workshopId,
        getUserVote: (state) => state.userVote,
        getCustomerId: (state) => state.customerId,
    }
});
